import { useEffect, useId } from "react";

import { observer } from "mobx-react-lite";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store/RootStoreProvider";

import { DocItem } from "../documents/DocumentItem";
import { FolderItem } from "../documents/FolderItem";
import { NavInstructions } from "../NavInstructions";
import { NavTree } from "../useSidebarNav";

import type { Folder } from "@/api";

export const ProfileDocuments = observer(() => {
  const { publicProfile, documentTree } = useStore();
  const { docId } = useParams<{ docId: string }>();
  const t = useTranslations();
  const instructionsId = useId();

  const sharedFolder = {
    typename: "Folder",
    uid: "shared",
    title: t("share.sharedWithMe"),
    files: publicProfile.shared,
  } as Folder;

  useEffect(() => {
    if (docId && publicProfile.published) {
      documentTree.setCurrentDoc(docId, [publicProfile.published]);
    }
  }, [docId, publicProfile.published, documentTree]);

  const files = publicProfile.published?.files;
  if (!files?.length) {
    return null;
  }

  return (
    <>
      <div className="mx-2.5 mb-2.5 h-px bg-block-border" />
      <Sidebar.Scrollable
        as="nav"
        aria-label={t("documentsSidebar.documentTreePanel")}
        className="p-2.5 pt-0"
        aria-describedby={instructionsId}
      >
        <NavInstructions id={instructionsId} />
        <NavTree aria-label={t("documentsSidebar.documentTreePanel")}>
          {publicProfile.shared.length > 0 && (
            <div className="my-2.5">
              <div className="rounded-8 -m-1.25 p-1.25 bg-gray-100 dark:bg-gray-900">
                <FolderItem
                  iconName="Shared"
                  folder={sharedFolder}
                  invertedColor
                  draggable={false}
                  renderContext="public-profile"
                />
              </div>
            </div>
          )}
          {files.map((file) => {
            if (file.typename === "DocumentShort") {
              return (
                <DocItem
                  key={file.uid}
                  docItem={file}
                  invertedColor={false}
                  draggable={false}
                  renderContext="public-profile"
                />
              );
            }
            if (file.typename === "Folder") {
              return (
                <FolderItem
                  key={file.uid}
                  folder={file}
                  invertedColor={false}
                  draggable={false}
                  renderContext="public-profile"
                />
              );
            }
            return null;
          })}
        </NavTree>
      </Sidebar.Scrollable>
    </>
  );
});
